import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import isYesterday from 'dayjs/plugin/isYesterday'
import relativeTime from 'dayjs/plugin/relativeTime'

import { CATEGORY_COLORS, ColorSlot, PALETTE } from './colors'

export type { ColorSlot } from './colors'
export { PALETTE } from './colors'

dayjs.extend(relativeTime)
dayjs.extend(isToday)
dayjs.extend(isYesterday)

function hashStr(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

export function colorFor(key: string | null | undefined): ColorSlot {
  if (!key) return PALETTE[0]
  return CATEGORY_COLORS[key] || PALETTE[hashStr(key) % PALETTE.length]
}

export function humanize(s: string | null | undefined): string {
  return String(s || '')
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function monogram(s: string | null | undefined): string {
  if (!s) return '??'
  const parts = String(s)
    .split(/[\s@._-]+/)
    .filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return parts[0].slice(0, 2).toUpperCase()
}

export function fmtRelative(iso: string): string {
  return dayjs(iso).fromNow()
}

export function fmtClock(iso: string): string {
  return dayjs(iso).format('HH:mm:ss')
}

export function fmtDateDivider(iso: string): string {
  const d = dayjs(iso)
  const dayStr = d.format('dddd, MMMM D')
  if (d.isToday()) return `Today — ${dayStr}`
  if (d.isYesterday()) return `Yesterday — ${dayStr}`
  return dayStr
}

export function dateKey(iso: string): string {
  return dayjs(iso).format('YYYY-MM-DD')
}

export type TimePreset = { key: string; label: string; ms?: number; today?: boolean }

export const TIME_PRESETS: TimePreset[] = [
  { key: 'hour', label: 'Last hour', ms: 60 * 60 * 1000 },
  { key: 'today', label: 'Today', today: true },
  { key: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '30d', label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
  { key: '90d', label: 'Last 90 days', ms: 90 * 24 * 60 * 60 * 1000 },
]

function escHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string),
  )
}

export function syntaxJson(obj: unknown): string {
  const json = JSON.stringify(obj, null, 2) || ''
  return escHtml(json)
    .replace(/(&quot;[^&]*?&quot;)(\s*:)/g, '<span class="k">$1</span>$2')
    .replace(/:\s*(&quot;.*?&quot;)/g, ': <span class="s">$1</span>')
    .replace(/:\s*(-?\d+(?:\.\d+)?)/g, ': <span class="n">$1</span>')
    .replace(/:\s*(true|false|null)/g, ': <span class="b">$1</span>')
}

export function presetToRange(key: string): { from: string; to: string } | null {
  const preset = TIME_PRESETS.find((p) => p.key === key)
  if (!preset) return null
  const now = dayjs()
  if (preset.today) {
    return { from: now.startOf('day').toISOString(), to: now.toISOString() }
  }
  return { from: now.subtract(preset.ms || 0, 'millisecond').toISOString(), to: now.toISOString() }
}
